import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import API from '../../../services/api';

let stripePromise = null;

const PaymentStep = ({ bookingData, onComplete, onBack }) => {
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [testMode, setTestMode] = useState(false);

  useEffect(() => {
    initializePayment();
  }, []);

  const initializePayment = async () => {
    try {
      setLoading(true);
      
      // Step 1: Get Stripe publishable key
      const keyResponse = await API.settings.getStripeKey();
      const { publishable_key, test_mode } = keyResponse.data;
      
      if (!publishable_key) {
        throw new Error('Stripe is not configured. Please contact support.');
      }

      // Step 2: Initialize Stripe
      stripePromise = loadStripe(publishable_key);
      setTestMode(test_mode);
      
      // Step 3: Create payment intent
      const intentResponse = await API.bookings.createPaymentIntent({
        booking_reference: bookingData.reference
      });
      
      setClientSecret(intentResponse.data.client_secret);
      setPaymentIntentId(intentResponse.data.payment_intent_id);
      setError(null);
    } catch (err) {
      console.error('Payment initialization error:', err);
      setError(err.message || 'Failed to initialize payment system');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-gray-800 rounded-2xl p-8 max-w-4xl mx-auto">
        <div className="flex flex-col items-center justify-center py-20">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-emerald-600 mb-4"></div>
          <p className="text-gray-400 text-lg">Initializing secure payment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-800 rounded-2xl p-8 max-w-4xl mx-auto">
        <div className="bg-red-500/20 border border-red-500 rounded-lg p-6 text-center">
          <svg className="w-16 h-16 text-red-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-red-400 mb-4">{error}</p>
          <button onClick={initializePayment} className="px-6 py-3 bg-red-600 hover:bg-red-700 rounded-lg font-bold transition-colors">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-2xl p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-3xl font-playfair font-bold text-white mb-2">
          Secure Payment
        </h2>
        <p className="text-gray-400">
          Complete your booking with Stripe - 100% secure and encrypted
        </p>
      </div>

      {/* Test Mode Notice */}
      {testMode && (
        <div className="mb-6 p-4 bg-yellow-500/20 border border-yellow-500 rounded-lg">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-yellow-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="text-yellow-200 text-sm">
              <strong>🧪 Test Mode:</strong> Use test card <span className="font-mono">4242 4242 4242 4242</span> with any future date and CVC.
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Payment Form */}
        <div className="lg:col-span-2">
          {clientSecret && stripePromise && (
            <Elements 
              stripe={stripePromise} 
              options={{ 
                clientSecret,
                appearance: {
                  theme: 'night',
                  variables: {
                    colorPrimary: '#10b981',
                    colorBackground: '#374151',
                    colorText: '#ffffff',
                    colorDanger: '#ef4444',
                    borderRadius: '8px',
                  }
                }
              }}
            >
              <StripePaymentForm 
                bookingData={bookingData}
                paymentIntentId={paymentIntentId}
                onComplete={onComplete}
                onBack={onBack}
                testMode={testMode}
              />
            </Elements>
          )}
        </div>

        {/* Order Summary */}
        <OrderSummary bookingData={bookingData} testMode={testMode} />
      </div>
    </div>
  );
};

// Stripe Payment Form Component
const StripePaymentForm = ({ bookingData, paymentIntentId, onComplete, onBack, testMode }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setErrorMessage('');

    try {
      // Submit the form
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setErrorMessage(submitError.message);
        setProcessing(false);
        return;
      }

      // Confirm payment with Stripe
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
        confirmParams: {
          return_url: window.location.origin + '/booking/success',
        },
      });

      if (error) {
        setErrorMessage(error.message);
        setProcessing(false);
     } else if (paymentIntent && paymentIntent.status === 'succeeded') {
  // Confirm with backend
  console.log('Payment succeeded:', {
    paymentIntentId: paymentIntent.id,
    bookingReference: bookingData.reference
  });
  
  try {
    await API.bookings.confirmPayment({
      booking_reference: bookingData.reference,
      payment_intent_id: paymentIntent.id
    });
    
    onComplete({ paymentCompleted: true });
  } catch (confirmError) {
    console.error('Payment confirmation error:', confirmError);
    setErrorMessage(
      'Payment successful but confirmation failed. Please contact support with reference: ' + 
      bookingData.reference
    );
    setProcessing(false);
  }
}
    } catch (err) {
      console.error('Payment error:', err);
      setErrorMessage(err.message || 'Payment failed. Please try again.');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-gray-700 rounded-xl p-6">
        <PaymentElement />
      </div>

      {errorMessage && (
        <div className="bg-red-500/20 border border-red-500 rounded-lg p-4">
          <p className="text-red-400 text-sm">{errorMessage}</p>
        </div>
      )}

      <div className="flex gap-4">
        <button
          type="button"
          onClick={onBack}
          disabled={processing}
          className="px-8 py-4 bg-gray-700 hover:bg-gray-600 rounded-lg font-bold text-lg transition-colors disabled:opacity-50"
        >
          Back
        </button>
<button
  type="submit"
  disabled={!stripe || processing}
  className="flex-1 px-8 py-4 bg-emerald-600 hover:bg-emerald-700 rounded-lg font-bold text-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-3"
>
  {processing ? (
    <>
      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
      Processing...
    </>
  ) : (
    <>
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
      </svg>
      {/* FIXED: Safe number display */}
      Pay ${parseFloat(bookingData.totalAmount || 0).toFixed(2)}
    </>
  )}
</button>
      </div>

      {testMode && (
        <div className="text-center text-yellow-400 text-sm">
          💳 Test Card: <span className="font-mono">4242 4242 4242 4242</span> | Exp: Any future date | CVC: Any 3 digits
        </div>
      )}
    </form>
  );
};

// Order Summary Component
const OrderSummary = ({ bookingData, testMode }) => {
    // FIXED: Safe number conversion
  const totalAmount = parseFloat(bookingData.totalAmount || 0);
  return (
    <div className="bg-gray-700 rounded-xl p-6 h-fit sticky top-8">
      <h3 className="text-xl font-bold text-white mb-4">Booking Summary</h3>
      
      {testMode && (
        <div className="mb-4 p-3 bg-yellow-500/20 border border-yellow-500 rounded-lg">
          <p className="text-yellow-200 text-xs font-bold">🧪 TEST MODE</p>
        </div>
      )}
      
      <div className="space-y-3 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-400">Tour:</span>
          <span className="text-white font-medium">{bookingData.tour?.name}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-400">Date:</span>
          <span className="text-white">{bookingData.selectedDate}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-400">Time:</span>
          <span className="text-white">{bookingData.selectedTime}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-400">Participants:</span>
          <span className="text-white">{bookingData.participants?.length || 0}</span>
        </div>
        
        <div className="border-t border-gray-600 my-4"></div>
        
        <div className="flex justify-between text-lg font-bold">
          <span className="text-white">Total:</span>
          {/* FIXED: Safe number display */}
          <span className="text-emerald-400">${totalAmount.toFixed(2)}</span>
        </div>
      </div>

      <div className="mt-6 pt-6 border-t border-gray-600">
        <div className="flex items-center gap-2 text-gray-400 text-xs mb-2">
          <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <span>256-bit SSL Encryption</span>
        </div>
        <div className="flex items-center gap-2 text-gray-400 text-xs mb-2">
          <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>PCI DSS Compliant</span>
        </div>
        <div className="flex items-center gap-2 text-gray-400 text-xs">
          <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span>Powered by Stripe</span>
        </div>
      </div>
    </div>
  );
};

export default PaymentStep;